// src/screens/Authenticated/Property/NewRegisterScreen/NewRegisterViewScreen.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Styles } from './style';
import { useNavigate, useParams } from 'react-router-dom';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { ImageGallery } from '@components/ImageUploader';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import { useUserStore } from '@stores/User/User';
import { UserRole } from '@stores/User/types';
import { usePropertyStore } from '@stores/Property/Property';
import { IAuthenticatedPropertyDetails } from '@stores/Property/types';

const NewRegisterViewScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { role } = useUserStore();
  const isAdmin = role === UserRole.ADMIN;

  const { fetchAuthenticatedPropertyDetails } = usePropertyStore();

  const {
    caracteristicas,
    chaves,
    cidades,
    codigosImovel,
    condicoesImovel,
    estados,
    tiposImovel,
    fetchCaracteristicas,
    fetchChaves,
    fetchCidades,
    fetchCodigosImovel,
    fetchCondicoesImovel,
    fetchTiposImovel
  } = useMetadataStore();

  const sortByOrdem = <T extends { ordem: number }>(arr: T[]) =>
    [...arr].sort((a, b) => a.ordem - b.ordem);

  const tiposOptions = useMemo(() => sortByOrdem(tiposImovel), [tiposImovel]);
  const codigosOptions = useMemo(
    () => sortByOrdem(codigosImovel),
    [codigosImovel]
  );
  const chavesOptions = useMemo(() => sortByOrdem(chaves), [chaves]);
  const condicoesOptions = useMemo(
    () => sortByOrdem(condicoesImovel),
    [condicoesImovel]
  );
  const cidadesOptions = useMemo(() => sortByOrdem(cidades), [cidades]);
  const estadosOptions = useMemo(() => sortByOrdem(estados), [estados]);
  const caracteristicasOptions = useMemo(
    () => sortByOrdem(caracteristicas),
    [caracteristicas]
  );

  const [formData, setFormData] =
    useState<IAuthenticatedPropertyDetails | null>(null);

  // Fetch metadata to show option labels
  useEffect(() => {
    fetchTiposImovel();
    fetchCondicoesImovel();
    fetchChaves();
    fetchCodigosImovel();
    fetchCidades();
    fetchCaracteristicas();
  }, [
    fetchTiposImovel,
    fetchCondicoesImovel,
    fetchChaves,
    fetchCodigosImovel,
    fetchCidades,
    fetchCaracteristicas
  ]);

  // Load property (always has id)
  useEffect(() => {
    const loadProperty = async () => {
      const propertyId = parseInt(id || '', 10);
      const propertyDetails =
        await fetchAuthenticatedPropertyDetails(propertyId);
      if (propertyDetails) {
        if (
          propertyDetails.dados_descricao &&
          !propertyDetails.dados_descricao.informacao_adicional
        ) {
          propertyDetails.dados_descricao.informacao_adicional = '';
        }
        if (!propertyDetails.cod_imovel_id) {
          propertyDetails.cod_imovel_id =
            codigosOptions.find((c) => c.cod === propertyDetails.cod_imovel)
              ?.id || null;
        }
        setFormData(propertyDetails);
      }
    };
    if (id) loadProperty();
  }, [id, fetchAuthenticatedPropertyDetails, codigosOptions]);

  const selectedStatus = useMemo<
    'DISPONIVEL' | 'INATIVO' | 'VENDIDO' | 'ALUGADO'
  >(() => {
    if (!formData) return 'INATIVO';
    if (formData.is_vendido) return 'VENDIDO';
    if (formData.is_alugado) return 'ALUGADO';
    if (formData.active) return 'DISPONIVEL';
    return 'INATIVO';
  }, [formData]);

  const formatBRL = (v?: number | null) =>
    typeof v === 'number' && !Number.isNaN(v)
      ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '';

  if (!formData) return null;

  return (
    <AuthenticatedLayout>
      <h1>VISUALIZAÇÃO DE IMÓVEL</h1>
      <Styles.RectangleBar />

      <form style={{ width: '100%' }}>
        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Código do Imóvel</Styles.Label>
            <Styles.Select
              style={{ width: '160px' }}
              name="cod_imovel"
              value={
                typeof formData.cod_imovel === 'string'
                  ? formData.cod_imovel
                  : formData.cod_imovel?.titulo || ''
              }
              disabled>
              <option value="">Selecione</option>
              {codigosOptions.map((c) => (
                <option key={c.id} value={c.cod}>
                  {c.cod}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Título</Styles.Label>
            <Styles.Input
              style={{ width: '700px' }}
              type="text"
              name="titulo"
              value={formData.titulo}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer style={{ width: isMobile ? '100%' : '115px' }}>
            <Styles.Label>ID</Styles.Label>
            <Styles.Input type="text" name="id" value={formData.id} disabled />
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Data de entrada</Styles.Label>
            <Styles.Input
              type="date"
              name="data_entrada"
              value={
                formData.data_entrada
                  ? new Date(formData.data_entrada).toISOString().split('T')[0]
                  : ''
              }
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Área Total (m²)</Styles.Label>
            <Styles.Input
              name="area_total"
              value={formData.area_total || ''}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Área Construída (m²)</Styles.Label>
            <Styles.Input
              type="text"
              name="area_construida"
              value={formData.area_construida}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Quartos</Styles.Label>
            <Styles.Select
              name="quartos"
              value={Number(formData.quartos) || 0}
              disabled>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Suítes</Styles.Label>
            <Styles.Select
              name="suites"
              value={Number(formData.suites) || 0}
              disabled>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Vagas</Styles.Label>
            <Styles.Select
              name="vagas"
              value={Number(formData.vagas) || 0}
              disabled>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Banheiros</Styles.Label>
            <Styles.Select
              name="banheiros"
              value={Number(formData.banheiros) || 0}
              disabled>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Tipo do Imóvel</Styles.Label>
            <Styles.Select
              name="tipo_imovel"
              value={
                formData.dados_comercializacao?.tipo_imovel?.titulo ||
                formData.tipo_imovel?.titulo ||
                ''
              }
              disabled>
              <option value="">Selecione</option>
              {tiposOptions.map((t) => (
                <option key={t.id} value={t.titulo}>
                  {t.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Nome do Condomínio</Styles.Label>
            <Styles.Input
              style={{ width: '350px' }}
              type="text"
              name="dados_localizacao.nome_condominio"
              value={formData.dados_localizacao?.nome_condominio || ''}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Chave</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.chave_id"
              value={
                formData.dados_comercializacao?.chave_id !== null &&
                formData.dados_comercializacao?.chave_id !== undefined
                  ? String(formData.dados_comercializacao.chave_id)
                  : ''
              }
              disabled>
              <option value="">Selecione</option>
              {chavesOptions.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Condições</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.condicao_id"
              value={
                formData.dados_comercializacao?.condicao_id !== null &&
                formData.dados_comercializacao?.condicao_id !== undefined
                  ? String(formData.dados_comercializacao.condicao_id)
                  : ''
              }
              disabled>
              <option value="">Selecione</option>
              {condicoesOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Mobiliado</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.is_mobiliada"
              value={String(
                Boolean(formData.dados_comercializacao?.is_mobiliada)
              )}
              disabled>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Financia</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.is_financia"
              value={String(
                Boolean(formData.dados_comercializacao?.is_financia)
              )}
              disabled>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>CEP</Styles.Label>
            <Styles.Input
              type="text"
              name="dados_localizacao.cep"
              value={formData.dados_localizacao?.cep || ''}
              disabled
              style={{ width: isMobile ? '100%' : '115px' }}
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>UF</Styles.Label>
            <Styles.Select
              style={{ width: '100px' }}
              name="dados_localizacao.estado_id"
              value={String(formData.dados_localizacao?.estado_id || '')}
              disabled>
              <option value="">Selecione</option>
              {estadosOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.uf}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Cidade</Styles.Label>
            <Styles.Select
              style={{ width: '200px' }}
              name="dados_localizacao.cidade_id"
              value={String(formData.dados_localizacao?.cidade_id || '')}
              disabled>
              <option value="">Selecione</option>
              {cidadesOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Número</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '85px' }}
              type="text"
              name="dados_localizacao.numero"
              value={formData.dados_localizacao?.numero || ''}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Complemento</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '150px' }}
              type="text"
              name="dados_localizacao.complemento"
              value={formData.dados_localizacao?.complemento || ''}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Bairro</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '250px' }}
              type="text"
              name="dados_localizacao.bairro"
              value={formData.dados_localizacao?.bairro || ''}
              disabled
            />
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Rua</Styles.Label>
            <Styles.Input
              style={{ width: '600px' }}
              type="text"
              name="dados_localizacao.rua"
              value={formData.dados_localizacao?.rua || ''}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Condições de Pagamento</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxGridTwoCols>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={Boolean(formData.dados_pagamento?.is_a_vista)}
                    disabled
                  />
                  À Vista
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={Boolean(
                      formData.dados_pagamento?.is_financiamento
                    )}
                    disabled
                  />
                  Financiamento
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={Boolean(formData.dados_pagamento?.is_permuta)}
                    disabled
                  />
                  Aceita Permuta
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={Boolean(
                      formData.dados_pagamento?.is_somente_a_vista
                    )}
                    disabled
                  />
                  Somente à Vista
                </Styles.CheckboxLabel>
              </Styles.CheckboxGridTwoCols>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Valor Venda</Styles.Label>
            <Styles.Input
              name="valor_venda"
              value={formatBRL(formData.valor_venda)}
              disabled
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Valor Aluguel</Styles.Label>
            <Styles.Input
              name="valor_aluguel"
              value={formatBRL(formData.valor_aluguel)}
              disabled
            />
          </Styles.InputContainer>

          {isAdmin && (
            <Styles.InputContainer>
              <Styles.Label>Valor Proprietário</Styles.Label>
              <Styles.Input
                name="dados_privados.valor_proprietario"
                value={formatBRL(formData.dados_privados?.valor_proprietario)}
                disabled
              />
            </Styles.InputContainer>
          )}

          <Styles.InputContainer>
            <Styles.Label>Valor Substituto</Styles.Label>
            <Styles.Input
              name="valor_substituto"
              value={formatBRL(formData.valor_substituto)}
              disabled
            />
          </Styles.InputContainer>

          {isAdmin && (
            <Styles.InputContainer>
              <Styles.Label>Comissão</Styles.Label>
              <Styles.Input
                name="dados_privados.comissao"
                value={formatBRL(formData.dados_privados?.comissao)}
                disabled
              />
            </Styles.InputContainer>
          )}

          {isAdmin && (
            <Styles.InputContainer>
              <Styles.Label>% Comissão</Styles.Label>
              <Styles.Input
                type="number"
                name="dados_privados.porcentagem_comissao"
                value={Number(
                  formData.dados_privados?.porcentagem_comissao ?? 0
                )}
                disabled
              />
            </Styles.InputContainer>
          )}
        </Styles.FormSection>

        <Styles.FormSection>
          {isAdmin && (
            <Styles.InputContainer>
              <Styles.Label>Valor Condomínio</Styles.Label>
              <Styles.Input
                name="dados_privados.valor_condominio"
                value={formatBRL(formData.dados_privados?.valor_condominio)}
                disabled
              />
            </Styles.InputContainer>
          )}

          {isAdmin && (
            <Styles.InputContainer>
              <Styles.Label>Valor IPTU</Styles.Label>
              <Styles.Input
                name="dados_privados.valor_iptu"
                value={formatBRL(formData.dados_privados?.valor_iptu)}
                disabled
              />
            </Styles.InputContainer>
          )}

          <Styles.InputContainer>
            <Styles.Label>Placa</Styles.Label>
            <Styles.Select
              style={{ width: '100px' }}
              name="dados_comercializacao.is_placa"
              value={String(Boolean(formData.dados_comercializacao?.is_placa))}
              disabled>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Disponível Para</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxRow>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    name="is_venda"
                    checked={Boolean(formData.is_venda)}
                    disabled
                  />
                  Venda
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    name="is_aluguel"
                    checked={Boolean(formData.is_aluguel)}
                    disabled
                  />
                  Aluguel
                </Styles.CheckboxLabel>
              </Styles.CheckboxRow>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Características do Imóvel</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxGridGroup>
                {caracteristicasOptions.map((cat) => (
                  <Styles.CheckboxLabel key={cat.id}>
                    <Styles.CheckboxInput
                      type="checkbox"
                      checked={formData.caracteristicas.some(
                        (c) => c.id === cat.id
                      )}
                      disabled
                    />
                    {cat.titulo}
                  </Styles.CheckboxLabel>
                ))}
              </Styles.CheckboxGridGroup>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormColumn>
          <Styles.Label>Descrição</Styles.Label>
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '12px',
              minHeight: '300px',
              backgroundColor: '#fafafa'
            }}
            dangerouslySetInnerHTML={{
              __html: formData.dados_descricao?.descricao || ''
            }}
          />
        </Styles.FormColumn>

        {isAdmin && formData.dados_descricao.informacao_adicional != null && (
          <Styles.FormColumn style={{ marginTop: '20px' }}>
            <Styles.Label>Informação Adicional</Styles.Label>
            <Styles.TextArea
              name="descricao.informacao_adicional"
              value={formData.dados_descricao?.informacao_adicional}
              disabled
              style={{ height: '200px' }}
            />
          </Styles.FormColumn>
        )}

        {isAdmin && (
          <Styles.OwnerSection>
            <Styles.RectangleBar
              style={{
                marginTop: '30px',
                paddingTop: '10px',
                backgroundColor: '#D9D9D9'
              }}
            />
            <h1>DADOS DO PROPRIETÁRIO</h1>
            <Styles.OwnerInputRow>
              <Styles.InputContainer>
                <Styles.Label>Nome</Styles.Label>
                <Styles.Input
                  style={{ width: '500px' }}
                  type="text"
                  placeholder="Nome"
                  name="dados_privados.nome_proprietario"
                  value={formData.dados_privados?.nome_proprietario || ''}
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>E-mail</Styles.Label>
                <Styles.Input
                  type="email"
                  placeholder="E-mail"
                  style={{ width: '440px' }}
                  name="dados_privados.email_proprietario"
                  value={formData.dados_privados?.email_proprietario || ''}
                  disabled
                />
              </Styles.InputContainer>
            </Styles.OwnerInputRow>
            <Styles.OwnerInputRow>
              <Styles.InputContainer>
                <Styles.Label>RG</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="RG"
                  name="dados_privados.rg_proprietario"
                  value={formData.dados_privados?.rg_proprietario || ''}
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>CPF</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="CPF"
                  name="dados_privados.cpf_proprietario"
                  value={formData.dados_privados?.cpf_proprietario || ''}
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Tel. Residencial</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Tel. Residencial"
                  name="dados_privados.telefone_residencial_proprietario"
                  value={
                    formData.dados_privados
                      ?.telefone_residencial_proprietario || ''
                  }
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Tel. Comercial</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Tel. Comercial"
                  name="dados_privados.telefone_comercial_proprietario"
                  value={
                    formData.dados_privados?.telefone_comercial_proprietario ||
                    ''
                  }
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Celular 1</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Celular 1"
                  name="dados_privados.celular_primario_proprietario"
                  value={
                    formData.dados_privados?.celular_primario_proprietario || ''
                  }
                  disabled
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Celular 2</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Celular 2"
                  name="dados_privados.celular_secundario_proprietario"
                  value={
                    formData.dados_privados?.celular_secundario_proprietario ||
                    ''
                  }
                  disabled
                />
              </Styles.InputContainer>
            </Styles.OwnerInputRow>
          </Styles.OwnerSection>
        )}

        <ImageGallery photos={formData.fotos_galeria || []} />

        {isAdmin && (
          <Styles.StatusSection>
            <Styles.StatusRow>
              <Styles.FieldBlock>
                <Styles.Label>STATUS</Styles.Label>
                <Styles.Select
                  name="status"
                  value={selectedStatus}
                  disabled
                  style={{ width: '220px' }}>
                  <option value="DISPONIVEL">Ativo</option>
                  <option value="INATIVO">Inativo</option>
                  <option value="VENDIDO">Vendido</option>
                  <option value="ALUGADO">Alugado</option>
                </Styles.Select>
              </Styles.FieldBlock>
              <Styles.FieldBlock>
                <Styles.Label>DESTAQUE</Styles.Label>
                <Styles.Switch>
                  <Styles.SwitchInput
                    type="checkbox"
                    checked={Boolean(formData.destaque)}
                    disabled
                  />
                  <Styles.SwitchSlider />
                </Styles.Switch>
              </Styles.FieldBlock>
              <Styles.FieldBlock>
                <Styles.Label>EM NEGOCIAÇÃO</Styles.Label>
                <Styles.Switch>
                  <Styles.SwitchInput
                    type="checkbox"
                    checked={Boolean(formData.negotiating)}
                    disabled
                  />
                  <Styles.SwitchSlider />
                </Styles.Switch>
              </Styles.FieldBlock>
            </Styles.StatusRow>
          </Styles.StatusSection>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <Styles.CancelButton type="button" onClick={() => navigate(-1)}>
            VOLTAR
          </Styles.CancelButton>
        </div>
      </form>
    </AuthenticatedLayout>
  );
};

export default NewRegisterViewScreen;
